import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuthStore } from '../../../auth/state';
import DashboardHeader from '../../../components/DashboardHeader';
import './specialty.css';

function Specialty() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const iframeRef = useRef(null);
  
  // # Get iframe URL from environment variable
  const iframeUrl = process.env.REACT_APP_SPECIALTY_IFRAME_URL;
  
  // # Load user theme preferences
  useEffect(() => {
    const loadThemePreference = async () => {
      try {
        if (user?.uid) {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setIsDarkMode(userData.themes?.gen_theme === 'dark');
          }
        }
      } catch (error) {
        console.error('Error loading theme preference:', error);
      }
    };

    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Apply theme directly to iframe
      if (iframeRef.current) {
        applyThemeToIframe(iframeRef.current, newTheme);
      }
      
      if (user?.uid) {
        await setDoc(doc(db, 'users', user.uid), {
          themes: { gen_theme: newTheme ? 'dark' : 'light' }
        }, { merge: true });
      }
    } catch (error) {
      console.error('Error updating theme:', error);
    }
  };

  // # Function to apply theme directly to iframe
  const applyThemeToIframe = (iframe, darkMode) => {
    if (iframe) {
      try {
        // # Update iframe data-theme attribute
        iframe.setAttribute('data-theme', darkMode ? 'dark' : 'light');
        
        // # Update iframe style - remove filters, use proper CSS variables
        iframe.style.colorScheme = darkMode ? 'dark' : 'light';
        iframe.style.backgroundColor = darkMode ? 'var(--specialty-bg-primary)' : 'white';
        
        // # Attempt to inject theme styles into the iframe
        setTimeout(() => {
          try {
            if (iframe && iframe.contentDocument) {
              const style = document.createElement('style');
              if (darkMode) {
                // # Dark mode styles without filters
                style.textContent = `
                  /* # Dark mode base styles */
                  body, html {
                    background-color: var(--specialty-bg-primary) !important;
                    color-scheme: dark !important;
                    color: var(--specialty-text-primary) !important;
                  }
                  
                  /* # Dark mode text colors */
                  h1, h2, h3, h4, h5, h6, p, span, div {
                    color: var(--specialty-text-primary) !important;
                  }
                  
                  /* # Dark mode backgrounds */
                  .notion-page-content,
                  .notion-page-block {
                    background-color: var(--specialty-bg-primary) !important;
                  }
                  
                  /* # Dark mode for block elements */
                  .notion-callout-block {
                    background-color: var(--specialty-bg-secondary) !important;
                    border-color: var(--specialty-card-border) !important;
                  }
                  
                  /* # Tables in dark mode */
                  .notion-collection-table {
                    background-color: var(--specialty-bg-primary) !important;
                    border-color: var(--specialty-card-border) !important;
                  }
                  
                  .notion-collection-table th,
                  .notion-collection-table td {
                    border-color: var(--specialty-card-border) !important;
                    color: var(--specialty-text-primary) !important;
                  }
                  
                  /* # Links in dark mode */
                  a, a:visited {
                    color: #64B5F6 !important;
                  }
                `;
              } else {
                // # Light mode styles
                style.textContent = `
                  body, html {
                    background-color: white !important;
                    color-scheme: light !important;
                    color: #2c3e50 !important;
                  }
                  
                  h1, h2, h3, h4, h5, h6, p, span, div {
                    color: #2c3e50 !important;
                  }
                  
                  /* # Light mode backgrounds */
                  .notion-page-content,
                  .notion-page-block {
                    background-color: white !important;
                  }
                  
                  /* # Light mode for block elements */
                  .notion-callout-block {
                    background-color: #f8f9fa !important;
                    border-color: rgba(55, 53, 47, 0.1) !important;
                  }
                  
                  /* # Tables in light mode */
                  .notion-collection-table {
                    background-color: white !important;
                    border-color: rgba(55, 53, 47, 0.1) !important;
                  }
                  
                  a, a:visited {
                    color: #0b6bcb !important;
                  }
                `;
              }
              iframe.contentDocument.head.appendChild(style);
            }
          } catch (e) {
            // # Silently fail if cross-origin issues prevent access
          }
        }, 1000);
      } catch (e) {
        // # Silently fail if cross-origin issues prevent access
      }
    }
  };

  // # Handle iframe load events
  const handleIframeLoad = () => {
    if (iframeRef.current) {
      try {
        // # Apply theme directly to iframe
        applyThemeToIframe(iframeRef.current, isDarkMode);
        
        // # Try to access iframe content to inject error suppression and styling
        const style = document.createElement('style');
        style.textContent = `
          /* # Hide elements that might expose URLs */
          .notion-overlay-container, 
          .notion-login-prompt,
          .notion-help-button,
          .notion-cursor-listener,
          .notion-topbar,
          .notion-selectable[data-block-id],
          .notion-frame > div[style*="fixed"],
          .notion-frame > div[style*="absolute"],
          .notion-link-token,
          .notion-collection-card__content,
          .notion-header__icon-wrapper {
            display: none !important;
          }
          
          /* # Force theme colors */
          body, html {
            background-color: ${isDarkMode ? 'var(--specialty-bg-primary)' : 'white'} !important;
            color-scheme: ${isDarkMode ? 'dark' : 'light'} !important;
            color: ${isDarkMode ? 'var(--specialty-text-primary)' : '#2c3e50'} !important;
          }
        `;
        
        // # Attempt to inject the style into the iframe
        setTimeout(() => {
          try {
            if (iframeRef.current && iframeRef.current.contentDocument) {
              iframeRef.current.contentDocument.head.appendChild(style);
              
              // # Also try to intercept console methods in the iframe
              const script = document.createElement('script');
              script.textContent = `
                // # Block network requests that might leak URLs
                const originalFetch = window.fetch;
                window.fetch = function(url, options) {
                  // # Allow same origin requests
                  return originalFetch.apply(this, arguments);
                };
              `;
              iframeRef.current.contentDocument.head.appendChild(script);
            }
          } catch (e) {
            // # Silently fail if cross-origin issues prevent access
          }
        }, 1000);
      } catch (e) {
        // # Silently fail if cross-origin issues prevent access
      }
    }
  };

  // # Handle missing URL
  if (!iframeUrl) {
    return (
      <div className={`specialty_iframe-page ${isDarkMode ? 'specialty_dark-mode' : ''}`}>
        <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
        <main className="specialty_content">
          <div className="specialty_coming-soon">
            <h2>Coming Soon!</h2>
            <p>Information about choosing specialties is being prepared.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={`specialty_iframe-page ${isDarkMode ? 'specialty_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      <main className="specialty_content">
        {/* # Error suppressor element */}
        <div className="specialty_error-suppressor" aria-hidden="true"></div>
        
        <div className={`specialty_iframe-container ${isDarkMode ? 'specialty_dark-container' : ''}`}>
          <iframe 
            ref={iframeRef}
            src={iframeUrl}
            title="Choosing Specialty Content"
            className={isDarkMode ? 'specialty_dark-mode-iframe' : 'specialty_light-mode-iframe'}
            allowFullScreen
            referrerPolicy="no-referrer"
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            data-theme={isDarkMode ? 'dark' : 'light'}
            style={{
              colorScheme: isDarkMode ? 'dark' : 'light',
              backgroundColor: isDarkMode ? 'var(--specialty-bg-primary)' : 'white'
            }}
            loading="lazy"
            importance="high"
            onLoad={handleIframeLoad}
          />
        </div>
      </main>
    </div>
  );
}

export default Specialty; 